import { memo, useContext, useMemo } from 'react'
import type { ContactCenterUserContext as UserContext } from '@ucc/admin-global-state'

import AppsVersionContext from 'components/contexts/AppsVersionContext'
import ContactCenterUserContext from 'components/contexts/ContactCenterUserContext'
import config from 'config'
import { SubAppRouteConfig } from 'types/SubAppRouteConfig'
import ModuleFederationLoader from 'utils/ModuleFederationLoader'
import { joinUrl } from 'utils/urlUtils'

const useCCConfigurationConfig = () => {
  const { userContext } = useContext(ContactCenterUserContext)
  const { contactCenterTargetBuild } = useContext(AppsVersionContext)
  const uccUnifiedAdminCCConfiguration = useMemo(
    () => ({
      ...config.subAppsModuleFederation.uccUnifiedAdminCCConfiguration,
      url: joinUrl(config.subAppsModuleFederation.uccUnifiedAdminCCConfiguration.url, contactCenterTargetBuild),
    }),
    [contactCenterTargetBuild],
  )

  return {
    uccUnifiedAdminCCConfiguration,
    userContext,
  }
}

interface CCConfigurationRoutesComponentProps {
  uccUnifiedAdminCCConfiguration: any
  userContext: UserContext
  hideFooter?: boolean
}

const CCConfigurationRoutesComponent = memo(
  ({ uccUnifiedAdminCCConfiguration, userContext, hideFooter = false }: CCConfigurationRoutesComponentProps) => {
    return (
      <ModuleFederationLoader
        module="ccConfigurationApp"
        initSentry={false}
        subAppModuleFederation={uccUnifiedAdminCCConfiguration}
        userContext={userContext}
        hideFooter={hideFooter}
        key="ccConfigurationApp"
      />
    )
  },
)

const useCCConfigurationRoutes = (path: string): SubAppRouteConfig[] => {
  const { uccUnifiedAdminCCConfiguration, userContext } = useCCConfigurationConfig()
  const callQueuesPaths =
    uccUnifiedAdminCCConfiguration.routes
      ?.filter((route) => !route.startsWith('inbox'))
      ?.map((route) => `${path}/${route}`) ?? []
  const inboxPaths =
    uccUnifiedAdminCCConfiguration.routes
      ?.filter((route) => route.startsWith('inbox'))
      ?.map((route) => `${path}/${route}`) ?? []

  if (userContext) {
    return [
      {
        paths: callQueuesPaths,
        component: () => (
          <CCConfigurationRoutesComponent
            uccUnifiedAdminCCConfiguration={uccUnifiedAdminCCConfiguration}
            userContext={userContext}
          />
        ),
      },
      {
        paths: inboxPaths,
        component: () => (
          <CCConfigurationRoutesComponent
            uccUnifiedAdminCCConfiguration={uccUnifiedAdminCCConfiguration}
            userContext={userContext}
            hideFooter
          />
        ),
      },
    ]
  }

  return [
    {
      paths: [...callQueuesPaths, ...inboxPaths],
      component: () => <></>,
    },
  ]
}

export default useCCConfigurationRoutes
